import type { CSSProperties, FunctionComponent, PropsWithoutRef, ReactNode, Ref } from 'react';
import { forwardRef, useCallback, useLayoutEffect, useState } from 'react';

import { useConsolidatedRef, usePrevious, useTheme } from '../../hooks';
import type { AsProp, BaseProps, ForwardProps, PropsWithDefaults } from '../../types';

export interface ExpandCollapseProps extends AsProp, BaseProps {
  /** Content to expand/collapse. */
  children?: ReactNode;
  /**
   * If true,the content will be hidden.
   * @default false
   */
  collapsed?: boolean;
  /**
   * Used to determine the transition property.
   * @default 'height'
   */
  dimension?: 'height' | 'width';
  /** Minimum height/width to collapse to. This will set the starting collapsed size as well. */
  min?: string;
  /** Maximum height/width to expand to. Expanded state will expanded up until, but not beyond, the natural height/width of the element. */
  max?: string;
  /** Callback fired after content collapsed. */
  onAfterCollapse?: () => void;
  /** Callback fired after content expanded. */
  onAfterExpand?: () => void;
  /** Callback fired before content collapsed. */
  onBeforeCollapse?: () => void;
  /** Callback fired before content expanded. */
  onBeforeExpand?: () => void;
  /**
   * Determine the speed of animation. This value comes from `theme.base.animation.speed`.
   * @default '0.25s'
   */
  transitionSpeed?: string;
  /**
   * If true, removes the component from the real DOM when collapsed. For full virtual DOM unmount(if necessary), perform at a parent level.
   * @default false
   */
  nullWhenCollapsed?: boolean;
}

type ExpandCollapsePropsWithDefaults = PropsWithDefaults<
  ExpandCollapseProps,
  'collapsed' | 'dimension' | 'transitionSpeed' | 'nullWhenCollapsed'
>;

type Statuses = 'collapsed' | 'collapsing' | 'expanded' | 'expanding';

interface GetStyleProps
  extends Pick<
    ExpandCollapsePropsWithDefaults,
    'dimension' | 'transitionSpeed' | 'min' | 'max' | 'nullWhenCollapsed'
  > {
  status: Statuses;
  el?: HTMLElement | null;
  transitionTimingFunction?: string;
}

const getScrollSize = (
  el: HTMLElement,
  dimension: ExpandCollapsePropsWithDefaults['dimension']
): string => {
  if (!el) return '';
  return dimension === 'height' ? `${el.scrollHeight}px` : `${el.scrollWidth}px`;
};

const getStyle = (props: GetStyleProps): CSSProperties => {
  const {
    status,
    dimension,
    transitionSpeed,
    transitionTimingFunction,
    min,
    max,
    nullWhenCollapsed,
    el
  } = props;
  const style: CSSProperties = {};
  let maxSize = max;

  if (el && !max) {
    maxSize = getScrollSize(el, dimension!);
  }

  if (status === 'expanded' || status === 'collapsed') {
    if (max || min) {
      style[dimension as 'height' | 'width'] = status === 'expanded' ? maxSize : min || 0;
      style[`overflow${dimension === 'width' ? 'X' : 'Y'}`] = 'hidden';
    }

    if (status === 'collapsed' && !nullWhenCollapsed && !min) {
      style[dimension as 'height' | 'width'] = 0;
      style.overflow = 'hidden';
      style.visibility = 'hidden';
    }
  }

  if (status === 'expanding' || status === 'collapsing') {
    style[dimension as 'height' | 'width'] = status === 'expanding' ? maxSize : min || 0;
    style.overflow = 'hidden';
    style.transitionProperty = dimension;
    style.transitionDuration = `max(${transitionSpeed}, 0.01ms)`;
    style.transitionTimingFunction = transitionTimingFunction;
  }

  return style;
};

const ExpandCollapse: FunctionComponent<ExpandCollapseProps & ForwardProps> = forwardRef(
  function ExpandCollapse(props: PropsWithoutRef<ExpandCollapseProps>, refArg: Ref<HTMLElement>) {
    const {
      base: { animation: themeAnimation }
    } = useTheme();

    const {
      children,
      dimension = 'height',
      collapsed = false,
      onBeforeExpand,
      onAfterExpand,
      onBeforeCollapse,
      onAfterCollapse,
      transitionSpeed = themeAnimation.speed,
      min,
      max,
      nullWhenCollapsed = false,
      as: Tag = 'div',
      ...restProps
    } = props;

    const [status, setStatus] = useState<Statuses>(collapsed ? 'collapsed' : 'expanded');
    const styleStateHook = useState<CSSProperties>();
    const setStyle = styleStateHook[1];
    let style = styleStateHook[0];
    const previousStatus = usePrevious(status);
    const ref = useConsolidatedRef(refArg);

    const onTransitionEnd = useCallback(
      (e: TransitionEvent) => {
        if (e.target !== ref.current || e.propertyName !== dimension) return;
        setStatus(status === 'expanding' ? 'expanded' : 'collapsed');
      },
      [ref.current, status]
    );

    useLayoutEffect(() => {
      const wantsToCollapse = collapsed;
      const wantsToExpand = !collapsed;
      const canCollapse = ['expanded', 'expanding'].includes(status);
      const canExpand = ['collapsed', 'collapsing'].includes(status);

      if (wantsToExpand && canExpand) {
        onBeforeExpand?.();
        setStatus('expanding');
      } else if (wantsToCollapse && canCollapse) {
        onBeforeCollapse?.();
        setStatus('collapsing');
      }

      if (status === 'expanded' && previousStatus !== 'expanded') {
        onAfterExpand?.();
      } else if (status === 'collapsed' && previousStatus !== 'collapsed') {
        onAfterCollapse?.();
      }

      setStyle(
        getStyle({
          dimension,
          min,
          max,
          transitionSpeed,
          nullWhenCollapsed,
          status,
          el: ref.current,
          transitionTimingFunction: themeAnimation.timing.ease
        })
      );
    }, [collapsed, status, min, max]);

    if (status !== previousStatus && ['expanded', 'collapsed'].includes(previousStatus!)) {
      style = getStyle({
        dimension,
        min,
        max,
        transitionSpeed,
        nullWhenCollapsed,
        status: status === 'expanding' ? 'collapsing' : 'expanding',
        el: ref.current,
        transitionTimingFunction: themeAnimation.timing.ease
      });
    }

    return status === 'collapsed' && nullWhenCollapsed && !min ? null : (
      <Tag
        ref={ref}
        onTransitionEnd={onTransitionEnd}
        {...restProps}
        inert={status === 'collapsed' && !nullWhenCollapsed && !min ? '' : undefined}
        style={style}
      >
        {children}
      </Tag>
    );
  }
);

export default ExpandCollapse;
